import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { colors3 } from "../../colors/color";
import Printer2 from "../../components/Printer2";
import GraphLoader from "../../components/GraphLoader";
import { Grid, Button, ButtonGroup, Chip } from "@mui/material";
import { useSelector } from "react-redux";
import "./scbsmb.css";

const SmbStringLossPred = ({ title, scbsmbDetail, selectedLebel }) => {
  const { scbsmbLoss = [], loading } = useSelector((state) => state.scbsmbLoss);

  /* ###   Initialize State to animate Graph Bars and Lines   ### */
  const [showEfficiencyYearlyLoss, setShowEfficiencyYearlyLoss] = useState({});
  const [tooltipAdjust, setTooltipAdjust] = useState({ x: null, y: null });
  const [scbsmbStringDeviation, setScbsmbStringDeviation] = useState(null);

  // State for time period selection
  const [selectedPeriod, setSelectedPeriod] = useState("historical");
  const [selectedFilter, setSelectedFilter] = useState("all");

  // Get the current date range type from selectedLebel
  const currentDateRangeType = selectedLebel?.dateRangeType || "month";

  // Dynamic time period options based on dateRangeType
  const getTimePeriods = () => {
    switch (currentDateRangeType) {
      case "5_min":
        return [
          { label: "Historical", value: "historical", description: "View past data (5-min)" },
          { label: "Next 1 Hour", value: "1hour", description: "Forecast next 1 hour" },
          { label: "Next 2 Hours", value: "2hours", description: "Forecast next 2 hours" },
        ];
      case "day":
        return [
          { label: "Historical", value: "historical", description: "View past data (daily)" },
          { label: "Next 7 Days", value: "7days", description: "Forecast next 7 days" },
          { label: "Next 30 Days", value: "30days", description: "Forecast next 30 days" },
        ];
      case "month":
        return [
          { label: "Historical", value: "historical", description: "View past data (monthly)" },
          { label: "Next 6 Months", value: "6months", description: "Forecast next 6 months" },
          { label: "Next Year", value: "year", description: "Forecast next year" },
        ];
      case "year":
        return [
          { label: "Historical", value: "historical", description: "View past data (yearly)" },
          { label: "Next 2 Years", value: "2years", description: "Forecast next 2 years" },
          { label: "Next 5 Years", value: "5years", description: "Forecast next 5 years" },
        ];
      default:
        return [
          { label: "Historical", value: "historical", description: "View past data" },
          { label: "Next 6 Months", value: "6months", description: "Forecast next 6 months" },
          { label: "Next Year", value: "year", description: "Forecast next year" },
        ];
    }
  };

  const timePeriods = getTimePeriods();

  // Filter options configuration
  const filterOptions = [
    { value: "all", label: "Show All" },
    { value: "2", label: "Loss > 2%" },
    { value: "5", label: "Loss > 5%" },
    { value: "10", label: "Loss > 10%" }
  ];

  // Helper function to get slice limit based on period and date range type
  const getSliceLimit = (period) => {
    switch (period) {
      case "1hour": return 12;
      case "2hours": return 24;
      case "7days": return 7;
      case "30days": return 30;
      case "6months": return 6;
      case "year": return 12;
      case "2years": return 2;
      case "5years": return 5;
      default: return null;
    }
  };

  // Helper function to safely format numbers
  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return null;
    }
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (typeof num !== 'number' || isNaN(num)) {
      return null;
    }
    return num.toFixed(2);
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            minWidth: "200px",
          }}
        >
          <p style={{ margin: "0 0 5px 0", fontWeight: "bold" }}>
            {label}
          </p>
          {payload.map((entry, index) => {
            const hasNoData = entry.value === null || entry.value === undefined || entry.value === "N/A";
            return (
              <p
                key={index}
                style={{
                  margin: "2px 0",
                  color: entry.color,
                  fontSize: "0.8rem",
                  fontStyle: hasNoData ? "italic" : "normal",
                }}
              >
                {entry.name}: <strong>{hasNoData ? "No Data" : `${formatNumber(entry.value)}%`}</strong>
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  // Custom bar component that shows blank space for null values but maintains bar width
  const CustomBar = (props) => {
    const { fill, x, y, width, height, value } = props;

    if (value === null || value === undefined || isNaN(value)) {
      return (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill="transparent"
          stroke="none"
        />
      );
    }

    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        rx={2}
        ry={2}
      />
    );
  };

  /* ###   Reference for Graph container TO Download In Jpg and Svg format   ### */
  const graphRef = useRef(null);

  useEffect(() => {
    if (scbsmbDetail.scbsmbType === "SMB") {
      if (scbsmbLoss?.length > 0) {
        let obj = {};
        Object.keys(scbsmbLoss[0] || {}).forEach((e) => {
          if (e !== "Date") {
            obj[e] = true;
          }
        });
        obj["all"] = true;

        setShowEfficiencyYearlyLoss(obj);
        setScbsmbStringDeviation(scbsmbLoss);
      }
    }
  }, [scbsmbLoss]);

  // Filter data based on selected time period and value filter
  const filteredScbsmbStringDeviation = useMemo(() => {
    if (!scbsmbStringDeviation || scbsmbStringDeviation.length === 0) return [];

    let periodFilteredData = [...scbsmbStringDeviation];

    if (selectedPeriod === "historical") {
      periodFilteredData = periodFilteredData.filter(item =>
        Object.keys(item).some(key => key !== "Date" && !key.includes("Forecast"))
      );
    } else {
      const sliceLimit = getSliceLimit(selectedPeriod);
      periodFilteredData = periodFilteredData.filter(item =>
        Object.keys(item).some(key => key !== "Date" && key.includes("Forecast"))
      );
      if (sliceLimit) {
        periodFilteredData = periodFilteredData.slice(0, sliceLimit);
      }
    }

    if (selectedFilter === "all") {
      return periodFilteredData;
    }

    const threshold = parseFloat(selectedFilter);

    return periodFilteredData.map(item => {
      const filteredItem = { Date: item.Date };

      Object.keys(item).forEach(key => {
        if (key !== "Date" && !isNaN(item[key])) {
          const value = parseFloat(item[key]);
          if (value > threshold) {
            filteredItem[key] = item[key];
          } else {
            filteredItem[key] = null;
          }
        }
      });

      return filteredItem;
    });
  }, [scbsmbStringDeviation, selectedPeriod, selectedFilter]);

  const todayDate = new Date().toLocaleDateString();
  const startDate = selectedLebel?.startDate
    ? new Date(selectedLebel?.startDate).toLocaleDateString()
    : null;
  const endDate = selectedLebel?.endDate
    ? new Date(selectedLebel?.endDate).toLocaleDateString()
    : null;

  // Get unique keys for the chart
  const chartKeys = useMemo(() => {
    if (!filteredScbsmbStringDeviation?.length) return [];
    return Object.keys(filteredScbsmbStringDeviation[0] || {}).filter(e => e !== "Date");
  }, [filteredScbsmbStringDeviation]);

  // Calculate min and max values
  const { minValue, maxValue } = useMemo(() => {
    if (!filteredScbsmbStringDeviation?.length) return { minValue: 0, maxValue: 10 };
    
    let minVal = Infinity;
    let maxVal = -Infinity;
    
    filteredScbsmbStringDeviation.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== "Date" && !isNaN(item[key]) && item[key] !== null) {
          const val = parseFloat(item[key]);
          if (val < minVal) minVal = val;
          if (val > maxVal) maxVal = val;
        }
      });
    });
    
    if (minVal === Infinity) minVal = 0;
    if (maxVal === -Infinity) maxVal = 10;
    
    return {
      minValue: Math.floor(minVal) - 2,
      maxValue: Math.ceil(maxVal) + 2
    };
  }, [filteredScbsmbStringDeviation]);

  // Calculate total count for summary
  const totalCount = useMemo(() => {
    return scbsmbStringDeviation?.length || 0;
  }, [scbsmbStringDeviation]);

  const filteredCount = useMemo(() => {
    return filteredScbsmbStringDeviation?.length || 0;
  }, [filteredScbsmbStringDeviation]);

  // Handle period change
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    setSelectedFilter(e.target.value);
  };

  // Get display unit for the title
  const getDisplayUnit = () => {
    switch (currentDateRangeType) {
      case "5_min": return "5-min";
      case "day": return "Daily";
      case "month": return "Monthly";
      case "year": return "Yearly";
      default: return "Monthly";
    }
  };

  // Get unit label for summary
  const getUnitLabel = () => {
    switch (currentDateRangeType) {
      case "5_min": return "intervals";
      case "day": return "days";
      case "month": return "months";
      case "year": return "years";
      default: return "months";
    }
  };

  // Reset selected period when date range type changes
  useEffect(() => {
    setSelectedPeriod("historical");
  }, [currentDateRangeType]);

  const renderBarLabel = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "";
    return formatNumber(value);
  };

  return (
    <>
      <Grid container xl={12} textAlign={"center"}>
        <Grid container xl={12}>
          <Grid
            item
            xl={11.8}
            lg={11.8}
            md={11.8}
            sm={11.8}
            xs={11.8}
            style={{
              background: "linear-gradient(to bottom, rgb(3, 99, 125),rgb(4, 128, 162),rgb(4, 135, 172))",
              margin: "0 auto 0.5vmax",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0.5vmax",
            }}
          >
            <Grid
              item
              xs={12}
              style={{
                background: "white",
                padding: "0.5vmax 0.5vmax",
                position: "relative",
                overflow: "auto",
              }}
            >
              {/* Header with Time Period Selection */}
              <div
                style={{
                  padding: "1rem 1.5rem",
                  position: "sticky",
                  left: "0",
                  right: "0",
                  top: "0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  textAlign: "center",
                  zIndex: "10",
                  backgroundColor: "white",
                  flexWrap: "wrap",
                  gap: "1rem",
                }}
              >
                {/* Left side - Time Period Selection */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.5rem" }}>
                  <label style={{ fontSize: "0.9rem", fontWeight: 500, color: "#333" }}>
                    Time Period ({getDisplayUnit()}):
                  </label>
                  <ButtonGroup variant="outlined" size="small" style={{ display: "flex", flexWrap: "wrap" }}>
                    {timePeriods.map((period) => (
                      <Button
                        key={period.value}
                        onClick={() => handlePeriodChange(period.value)}
                        variant={selectedPeriod === period.value ? "contained" : "outlined"}
                        style={{
                          backgroundColor: selectedPeriod === period.value ? "#1890ff" : "transparent",
                          color: selectedPeriod === period.value ? "white" : "#666",
                          borderColor: "#d0d0d0",
                          fontSize: "0.8rem",
                          padding: "4px 12px",
                          minWidth: "90px",
                        }}
                        title={period.description}
                      >
                        {period.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>

                {/* Center - Title */}
                <div style={{ flex: 1, textAlign: "center" }}>
                  <h3 style={{ margin: "0 0 5px 0", fontSize: "1rem" }}>
                    String Current Avg. Loss (%) - {getDisplayUnit()} Data
                    {scbsmbDetail?.scbsmbType === "SMB" && ` (${title})`}
                    {selectedFilter !== "all" && (
                      <Chip
                        label="Filtered"
                        size="small"
                        style={{
                          marginLeft: "8px",
                          backgroundColor: "#fff2e8",
                          color: "#fa541c",
                          borderColor: "#ffbb96",
                          fontSize: "0.7rem",
                          height: "20px",
                        }}
                        variant="outlined"
                      />
                    )}
                  </h3>
                  <p style={{ margin: 0, fontSize: "0.8rem", color: "#666" }}>
                    {timePeriods.find(p => p.value === selectedPeriod)?.description}
                  </p>
                </div>

                {/* Right side - Filter and Printer */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.5rem" ,justifyContent:"center"}}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <label htmlFor="smb-string-filter" style={{ fontSize: "0.85rem", fontWeight: 500, color: "#333" }}>
                      Filter:
                    </label>
                    <select
                      id="smb-string-filter"
                      value={selectedFilter}
                      onChange={handleFilterChange}
                      style={{
                        padding: "0.4rem 0.8rem",
                        border: "1px solid #d9d9d9",
                        borderRadius: "4px",
                        fontSize: "0.8rem",
                        backgroundColor: "white",
                        cursor: "pointer",
                        minWidth: "160px",
                      }}
                    >
                      {filterOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>

                    {selectedFilter !== "all" && totalCount > 0 && (
                      <Chip
                        label={`${filteredCount}/${totalCount} ${getUnitLabel()}`}
                        size="small"
                        style={{
                          fontSize: "0.7rem",
                          backgroundColor: "#e6f7ff",
                          color: "#1890ff",
                          borderColor: "#91d5ff",
                          height: "22px",
                        }}
                        variant="outlined"
                      />
                    )}
                  </div>

                    <Printer2
                      dataSetDetail={{
                        data: filteredScbsmbStringDeviation || [],
                        name: `SMB String Deviation - ${getDisplayUnit()} (${selectedPeriod !== "historical" ? "Forecasted" : "Historical"})`,
                        ref: graphRef,
                        refCurrent: graphRef.current?.container,
                      }}
                    />
                </div>
              </div>

              {/* Custom Clickable Legend Container - Fixed Position */}
              {chartKeys.length > 0 && (
                <div
                  style={{
                    position: "sticky",
                    left: "0",
                    top: "85px",
                    zIndex: "1",
                    backgroundColor: "rgba(255, 255, 255, 0.98)",
                    padding: "10px 16px",
                    borderBottom: "1px solid #f0f0f0",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginBottom: "10px",
                    maxHeight: "80px",
                    overflowY: "auto",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                    {chartKeys.map((key) => (
                      <div
                        key={key}
                        onClick={() => {
                          setShowEfficiencyYearlyLoss(prev => ({
                            ...prev,
                            [key]: !prev[key]
                          }));
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                          userSelect: "none",
                          padding: "4px 8px",
                          borderRadius: "16px",
                          backgroundColor: showEfficiencyYearlyLoss[key] ? "transparent" : "#f5f5f5",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div
                          style={{
                            width: "12px",
                            height: "12px",
                            backgroundColor: "#203864",
                            borderRadius: "2px",
                            opacity: showEfficiencyYearlyLoss[key] ? 1 : 0.4,
                          }}
                        />
                        <span
                          style={{
                            fontSize: "0.7rem",
                            fontWeight: 500,
                            color: showEfficiencyYearlyLoss[key] ? "#333" : "#999",
                            textDecoration: showEfficiencyYearlyLoss[key] ? "none" : "line-through",
                          }}
                        >
                          {key}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Graph Container */}
              <div
                style={{
                  minWidth: "100%",
                  width: loading
                    ? "100%"
                    : filteredScbsmbStringDeviation?.length > 0
                      ? filteredScbsmbStringDeviation.length * chartKeys.length * 40 + "px"
                      : "100%",
                  paddingTop: "10px",
                }}
                className="forecasting-scbsmb-container"
              >
                <ResponsiveContainer width="100%" height={"100%"}>
                  {loading ? (
                    <div style={{ position: "absolute", top: "0", bottom: "0", right: "0", left: "0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <GraphLoader content={`${selectedPeriod !== "historical" ? "Forecasting" : "Loading"} String Deviation`} />
                    </div>
                  ) : filteredScbsmbStringDeviation?.length > 0 ? (
                    <ComposedChart
                      style={{ overflow: "hidden" }}
                      onMouseMove={(e) => {
                        if (e.isTooltipActive) {
                          setTooltipAdjust({
                            x: e.chartX > 20 ? e.chartX - 20 : e.chartX,
                            y: e.chartY > 150 ? 150 : e.chartY,
                          });
                        }
                      }}
                      data={filteredScbsmbStringDeviation || []}
                      margin={{ top: 15, right: 15, bottom: 15, left: 15 }}
                      ref={graphRef}
                    >
                      <CartesianGrid stroke="grey" strokeWidth={0.3} />
                      <XAxis
                        dataKey="Date"
                        fontSize={"0.8rem"}
                        fontWeight={600}
                        tickMargin={5}
                        minTickGap={10}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis
                        fontSize={"0.8rem"}
                        fontWeight={600}
                        width={40}
                        domain={[minValue, maxValue]}
                        tickLine={false}
                        label={{
                          value: "String Current Deviation (%)",
                          dy:80,
                          angle: -90,
                          position: "insideLeft",
                          offset: -10,
                          style: { fontSize: "0.8rem", fontWeight: "bold" },
                        }}
                      />
                      <Tooltip
                        position={tooltipAdjust}
                        content={<CustomTooltip />}
                        contentStyle={{ fontSize: "0.7rem" }}
                      />
                      <ReferenceLine y={0} stroke="#000" strokeWidth={1.5} />

                      {chartKeys.map((e, index) => {
                        if (e === "Date") return null;
                        return (
                          <Bar
                            maxBarSize={30}
                            key={index}
                            hide={showEfficiencyYearlyLoss[e] ? false : true}
                            dataKey={e}
                            fill={"#203864"}
                            shape={<CustomBar />}
                          >
                            <LabelList
                              dataKey={e}
                              position="top"
                              fontSize={"0.7rem"}
                              fontWeight={600}
                              fontFamily="Cambria, Cochin, Georgia, Times, 'Times New Roman', serif;"
                              formatter={renderBarLabel}
                            />
                          </Bar>
                        );
                      })}
                    </ComposedChart>
                  ) : (
                    <div style={{ height: "100%", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "1rem" }}>
                      <h3>
                        {selectedFilter !== "all"
                          ? "No data matches the filter criteria"
                          : scbsmbStringDeviation?.length === 0 && todayDate === startDate && todayDate === endDate
                            ? "Data Not Available On SCADA API"
                            : "No data available for the selected period"}
                      </h3>
                      {selectedFilter !== "all" && (
                        <button
                          onClick={() => setSelectedFilter("all")}
                          style={{
                            padding: "0.5rem 1rem",
                            backgroundColor: "#1890ff",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "0.9rem",
                          }}
                        >
                          Show All Data
                        </button>
                      )}
                    </div>
                  )}
                </ResponsiveContainer>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default SmbStringLossPred;